import { useState } from 'react';

import _ from 'lodash';

import { Menu as MenuIcon, Close, Facebook, Instagram, ExpandMore, Chat } from '@mui/icons-material';

import Autocomplete from '@mui/material/Autocomplete';

import {
    Drawer,
    Accordion,   
    AccordionSummary,
    AccordionDetails,    
    IconButton,
    Typography,
    InputBase,
    Link,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemButton
} from '@mui/material';

import Router from 'next/router';

import PropTypes from 'prop-types';

import { eventService } from '../../services/homii-services';

import useResponsive from '../../../utils/responsive';

import { Image } from '../shared';

import NavbarChatMenu from './navbar-chat-menu.component';

import { useIsomorphicLayoutEffect } from '../../../utils/hooks';

import { useCitiesData }  from '../../domain/city/city.domain';

const MAIN_NAV_HEIGHT_MOBILE = 66;

const MobileNavbar = ({ shouldScroll, isRoomPage, isBookingsPage }) => {
    
    const { data: cities } = useCitiesData();    

    const [cityOptions, setCityOptions] = useState(null);

    const [chatAnchorEl, setChatAnchorEl] = useState(null);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const [isScrolling, setIsScrolling] = useState(false);

    const [cityName, setCityName] = useState(null);

    const { isMobile } = useResponsive();

    const mobileMenuId = 'primary-search-account-menu-mobile';

    const handleScroll = (prevScrollPosition) => {
        const currentScrollPosition = window.pageYOffset;

        if (
            isMobile &&
            shouldScroll &&
            prevScrollPosition < currentScrollPosition &&
            currentScrollPosition > MAIN_NAV_HEIGHT_MOBILE &&
            !isScrolling
        ) {
            setIsScrolling(true);
        }
        if ((window.pageYOffset === 0 || !isMobile) && isScrolling) {
            setIsScrolling(false);
        }
    };

    useIsomorphicLayoutEffect(() => {
        setCityOptions(_.map(cities, (city) => city));
    }, [cities]);

    useIsomorphicLayoutEffect(() => {
        const prevScrollPosition = window.pageYOffset;
        window.addEventListener('scroll', () => handleScroll(prevScrollPosition));

        return () => window.removeEventListener('scroll', handleScroll);
    });

    const handleMobileMenuClose = () => {
        setIsMobileMenuOpen(false);
    };

    const handleMobileMenuOpen = (event) => {
        setIsMobileMenuOpen(Boolean(event.currentTarget));
    };

    const onSearchClick = () => {
        Router.push(`/bookings?city=${cityName}`);
        setCityName(null);
    };

    const onSearchInputChange = (event, value) => {
        const city = _.get(value, 'name');

        setCityName(null);
        _.forEach(cities, (item) => {
            const name = _.get(item, 'name');
            const formattedCity = _.upperCase(city.charAt(0)) + city.slice(1);
            if (_.lowerCase(name) === _.lowerCase(city)) {
                setCityName(formattedCity);
            }
        });
    };

    const onBrowseByCityClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/play/own-your-city');
    };

    const onBrowseByBuildingsClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/buildings');
    };

    const onContactUsClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/connect-with-us').then(() => window.scrollTo(0, 0));
    };

    const onBlogClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/blog').then(() => window.scrollTo(0, 0));
    };

    const onOwnYourCityClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/play/own-your-city').then(() => window.scrollTo(0, 0));
    };

    const onGalleryClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/play/gallery').then(() => window.scrollTo(0, 0));
    };

    const onFAQClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/faq').then(() => window.scrollTo(0, 0));
    };

    const onBookNowClick = () => {
        if (isRoomPage) {
            const element = document.getElementById('booking-quote-container');
            element.scrollIntoView();
        } else {
            Router.push('/bookings').then(() => window.scrollTo(0, 0));
        }
    };

    const onFindASpaceClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/find-a-space').then(() => window.scrollTo(0, 0));
    };

    const onOclubClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/o-club').then(() => window.scrollTo(0, 0));
    };

    const onFacebookClick = () => {
        eventService.logCustomEvent('facebook_click_navbar', {
            description: 'Clicked on Facebook from navbar',
        });
        window.open('//www.facebook.com/Homiilifestyle/', '_blank');
    };

    const onInstagramClick = () => {
        eventService.logCustomEvent('instagram_click_navbar', {
            description: 'Clicked on Instragram from navbar',
        });
        window.open('//www.instagram.com/homiilifestyle/?hl=en', '_blank');
    };

    const onAboutClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/about').then(() => window.scrollTo(0, 0));
    };

    const onLiveYourBestLifeClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/live/live-your-best-life').then(() => window.scrollTo(0, 0));
    };

    const onOurAppClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/live/our-app').then(() => window.scrollTo(0, 0));
    };

    const onYourOfficeClick = () => {
        setIsMobileMenuOpen(false);
        Router.push('/work/your-office').then(() => window.scrollTo(0, 0));
    };

    const handleChatMenuOpen = (event) => {
        setChatAnchorEl(event.currentTarget);
    };

    const renderBookNowButton = () => {
        if ((isMobile && isRoomPage) || isBookingsPage) {
            return null;
        }

        return (
            <Button
                type="button"
                variant="contained"
                id="mobile-book-now-button"
                onClick={onBookNowClick}
            >
                Book Now
            </Button>
        );
    };

    const renderSrollSearchBar = () => {
        return (
            <div className='mobile-navbar-search-container'>
                <IconButton id="mobile-search-button" onClick={onSearchClick} disabled={!cityName}>
                    <img className='mobile-navbar-search-icon' src="/assets/images/search-icon.png" alt="search" />
                </IconButton>

                <Autocomplete 
                    className='mobile-navbar-autocomplete'
                    id="combo-box-demo"
                    options={cityOptions}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    onChange={onSearchInputChange}
                    onKeyDown={(event) => {
                        if (event.key == 'Enter') {
                            onSearchClick();
                        }
                    }}
                    renderInput={(params) => {
                        // eslint-disable-next-line no-unused-vars
                        const { InputLabelProps, InputProps, ...rest } = params;
                        return <InputBase className='mobile-navbar-input-base' {...params.InputProps} {...rest} />;
                    }}
                />
            </div>
        );
    };

    const renderShouldNotScroll = () => {
        return (
            <>
                <div className='mobile-navbar-scroll-container'>
                    <div className='mobile-navbar-scroll-content-container'>
                        <header className='mobile-navbar-scroll-header-container mobile-navbar-scroll-header-position-fixed mobile-navbar-scroll-header-root mobile-navbar-scroll-header-paper-root mobile-navbar-scroll-header-color-primary' position="fixed">
                            <div className='mobile-navbar-tool-bar-regular'>
                                <button className="mobile-navbar-scroll-button" type="button" aria-label="open-mobile-drawer"
                                    onClick={handleMobileMenuOpen}
                                    aria-controls="primary-search-account-menu-mobile" aria-haspopup="true" id="mobile-menu-button">
                                    <span className="mobile-navbar-scroll-button-label">
                                        <svg className="mobile-navbar-scroll-svg-icon"
                                            focusable="false" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"></path>
                                        </svg>
                                    </span>
                                    <span className="mobile-navbar-scroll-root"></span>
                                </button>
                                <a href="/" id="mobile-navbar-scroll-homii-logo-link">
                                    <Image
                                        src="/assets/images/homii_logo.png"
                                        alt="HOMii Logo"
                                        width={82.25}
                                        height={22}
                                    />
                                </a>
                                <div className='mobile-navbar-spacer' />
                                {renderBookNowButton()}
                            </div>
                        </header>
                        <div className="MuiToolbar-root MuiToolbar-regular MuiToolbar-gutters"></div>
                        {renderDrawerMenu()}
                    </div>
                </div>
            </>
        );
    };

    const renderDrawerMenu = () => {
        return (
            <Drawer id={mobileMenuId} open={isMobileMenuOpen} onClose={handleMobileMenuClose} className='mobile-navbar-drawer'>
                <div>
                    <IconButton
                        color="inherit"
                        onClick={handleMobileMenuClose}
                        id="mobile-menu-close"
                    >
                        <Close className='mobile-navbar-close' />
                    </IconButton>
                </div>

                <Accordion className='mobile-navbar-accordion'>
                    <AccordionSummary
                        expandIcon={<ExpandMore className='mobile-navbar-expand-more' />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography className="drawer-heading">Find a Space</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List>
                            <ListItemButton                                
                                className="menu-list-item"
                                id="drawer-link-browse-by-city"
                                onClick={onBrowseByCityClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="Browse by City" />
                            </ListItemButton>
                            <ListItemButton                                
                                className="menu-list-item"
                                id="drawer-link-explore-buildings"
                                onClick={onBrowseByBuildingsClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="Explore Buildings" />
                            </ListItemButton>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion className='mobile-navbar-accordion'>
                    <AccordionSummary
                        expandIcon={<ExpandMore className='mobile-navbar-expand-more'/>}
                        aria-controls="panel2a-content"
                        id="panel2a-header"
                    >
                        <Typography className="drawer-heading">Why HOMii Life?</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List>
                            <h5>#Live</h5>
                            <ListItemButton
                                className="menu-list-item"
                                id="drawer-link-about-homii"
                                onClick={onAboutClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="About HOMii" />
                            </ListItemButton>
                            <ListItemButton
                                className="menu-list-item"
                                id="drawer-link-welcome-to-your-best-life"
                                onClick={onLiveYourBestLifeClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="Welcome To Your Best Life" />
                            </ListItemButton>
                            <ListItemButton
                                className="menu-list-item"
                                id="drawer-link-do-life-on-our-app"
                                onClick={onOurAppClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="Do Life On Our App" />
                            </ListItemButton>
                            <h5>#Work</h5>
                            <ListItemButton
                                className="menu-list-item"
                                id="drawer-link-your-homii-office"
                                onClick={onYourOfficeClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="Your HOMii Office" />
                            </ListItemButton>
                            <h5>#Play</h5>
                            <ListItemButton
                                className="menu-list-item"
                                id="drawer-link-own-your-city"
                                onClick={onOwnYourCityClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="Own Your City" />
                            </ListItemButton>
                            <ListItemButton
                                className="menu-list-item"
                                id="drawer-link-gallery"
                                onClick={onGalleryClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="Gallery" />
                            </ListItemButton>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion className='mobile-navbar-accordion'>
                    <AccordionSummary
                        expandIcon={<ExpandMore className='mobile-navbar-expand-more' />}
                        aria-controls="panel3a-content"
                        id="panel3a-header"
                    >
                        <Typography className="drawer-heading">Connect With Us</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List>
                            <h5>Important Stuff</h5>
                            <ListItemButton
                                className="menu-list-item"
                                id="drawer-link-blog"
                                onClick={onBlogClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="Blog" />
                            </ListItemButton>
                            <ListItemButton
                                className="menu-list-item"
                                id="drawer-link-contact-us"
                                onClick={onContactUsClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="Contact Us" />
                            </ListItemButton>
                            <ListItemButton
                                className="menu-list-item"
                                id="drawer-link-faq"
                                onClick={onFAQClick}
                            >
                                <ListItemText className='mobile-navbar-list-item-text' primary="FAQ" />
                            </ListItemButton>
                            <h5>Social</h5>
                            <IconButton
                                className="socials-button"
                                id="drawer-link-facebook"
                                onClick={onFacebookClick}
                            >
                                <Facebook className='mobile-navbar-facebook' />
                            </IconButton>
                            <IconButton
                                className="socials-button"
                                id="drawer-link-instagram"
                                onClick={onInstagramClick}
                            >
                                <Instagram className='mobile-navbar-instagram' />
                            </IconButton>
                            <IconButton
                                id="chat-menu-button"
                                aria-label="chat-menu-button"
                                aria-controls="primary-search-account-menu"
                                aria-haspopup
                                onClick={handleChatMenuOpen}
                                className="socials-button"
                            >
                                <Chat className='mobile-navbar-chat-icon' />
                            </IconButton>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <NavbarChatMenu anchorEl={chatAnchorEl} setAnchorEl={setChatAnchorEl} />

                <div className='mobile-navbar-drawer-button-container'>
                    <Button
                        variant="contained"
                        className="green-button"
                        id="drawer-link-find-a-space"
                        onClick={onFindASpaceClick}
                    >
                        Find A Space
                    </Button>
                </div>

                <div className='mobile-navbar-drawer-button-container'>
                    <Button
                        variant="contained"
                        className="green-button"
                        id="drawer-link-o-club"
                        onClick={onOclubClick}
                    >
                        O-Club
                    </Button>
                </div>

            </Drawer>
        );
    };

    // if (isScrolling) {
    //     return (
    //         <div>
    //             <header className='mobile-navbar-styled-app-bar mobile-navbar-app-bar-color-primary mobile-navbar-app-bar-position-fixed mobile-navbar-app-bar-root mobile-navbar-paper-elevation4 mobile-navbar-paper-root' position="fixed">
    //                 <div className='mobile-navbar-tool-bar-regular mobile-navbar-tool-bar-gutters mobile-navbar-tool-bar-root'>
    //                     <IconButton className='mobile-navbar-menu-button'
    //                         aria-controls={mobileMenuId}
    //                         aria-haspopup
    //                         onClick={handleMobileMenuOpen}
    //                         edge="start"
    //                         id="scrolling-menu-button"
    //                     >
    //                         <MenuIcon className='mobile-navbar-menu-icon' />
    //                     </IconButton>
    //                     {renderSrollSearchBar()}
    //                 </div>
    //             </header>
    //             {renderDrawerMenu()}
    //         </div>
    //     );
    // }

    return <>{renderShouldNotScroll()}</>;
};

export default MobileNavbar;

MobileNavbar.propTypes = {
    shouldScroll: PropTypes.bool,
    isRoomPage: PropTypes.bool,
    isBookingsPage: PropTypes.bool,
};

